<template>
  <v-card style="height: 100%">
    <project-flow-navigation-toolbar
      :title="parcelName"
    ></project-flow-navigation-toolbar>

    <v-toolbar dark dense>
      <v-tabs v-model="tab">
        <v-tab href="#details"> Details </v-tab>
        <v-tab href="#media"> Media </v-tab>
        <v-tab href="#pois"> Points of interest </v-tab>
      </v-tabs>
    </v-toolbar>

    <v-tabs-items
      style="height: calc(100% - 112px); position: relative"
      v-model="tab"
    >
      <v-tab-item style="height: 100%" value="details">
        <v-card class="full-height-card">
          <v-card-text>
            <v-row class="mt-1">
              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  dense
                  v-model="parcel.name"
                  label="Name"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-select
                  dense
                  :items="getStatuses"
                  v-model="parcel.statusId"
                  label="Status"
                  item-text="name"
                  item-value="id"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  dense
                  v-model="parcel.externalId"
                  label="External Id"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-subheader class="pl-0">Zoning parameters</v-subheader>
            <v-row>
              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  outlined
                  dense
                  type="number"
                  v-model.number="parcel.area"
                  label="Area (m²)"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  outlined
                  dense
                  type="number"
                  v-model.number="parcel.frontageWidth"
                  label="Frontage width (m)"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  outlined
                  dense
                  type="number"
                  v-model.number="parcel.maxFloors"
                  label="Max floors"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  outlined
                  dense
                  type="number"
                  v-model.number="parcel.maxOccupancy"
                  label="Max occupancy (%)"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  outlined
                  dense
                  type="number"
                  v-model.number="parcel.maxBuildIndex"
                  label="Max build index"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  outlined
                  dense
                  type="number"
                  v-model.number="parcel.buildingLineMin"
                  label="Building line min (m)"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  outlined
                  dense
                  type="number"
                  v-model.number="parcel.greeneryMin"
                  label="Greenery min (%)"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-select
                  outlined
                  dense
                  multiple
                  chips
                  small-chips
                  :items="roofTypes"
                  v-model="roofTypeArray"
                  label="Roof type"
                  clearable
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-textarea
                  outlined
                  dense
                  auto-grow
                  rows="2"
                  v-model="parcel.properties.description"
                  label="Description"
                  hint="Additional info shown on the plot card (e.g. how a figure was derived)"
                  persistent-hint
                ></v-textarea>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="4" md="3">
                <v-switch
                  inset
                  hide-details="auto"
                  v-model="parcel.energyCorridor"
                  label="Energy corridor"
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-switch
                  inset
                  hide-details="auto"
                  v-model="parcel.greenBelt"
                  label="Green belt"
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-switch
                  inset
                  hide-details="auto"
                  v-model="parcel.publish"
                  label="Published"
                ></v-switch>
              </v-col>
            </v-row>

            <v-row>
              <v-btn color="blue darken-1" class="ma-2" @click="generatePoi"
                >Generate Default Poi</v-btn
              >
            </v-row>
          </v-card-text>
          <v-spacer />
          <v-card-actions>
            <v-btn color="red darken-1" @click="deleteParcel">Delete</v-btn>
            <v-spacer></v-spacer>
            <v-fade-transition>
              <span
                v-if="changeDetected"
                class="caption amber--text d-flex align-center mr-2"
              >
                <v-icon x-small color="amber" class="mr-1"
                  >mdi-circle-medium</v-icon
                >Unsaved changes
              </span>
            </v-fade-transition>
            <v-btn v-if="changeDetected" text @click="discard">Discard</v-btn>
            <v-btn
              color="blue darken-1"
              :disabled="saveButtonDisabled"
              @click="save"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-tab-item>
      <v-tab-item value="media">
        <media
          v-if="showMedia"
          :media="this.$store.state.MediaStore.media"
          :is-visible="tab === 'media'"
        ></media>
        <v-card-subtitle v-if="!showMedia"
          >There is no default POI for this parcel yet — use "Generate Default
          Poi" on the Details tab to enable media.
        </v-card-subtitle>
      </v-tab-item>
      <v-tab-item value="pois">
        <v-list-item>
          <v-list-item-content>
            <poi-list
              :poi-list="this.$store.state.PoiStore.pois"
              parentType="parcel"
              :parent="this.$store.state.ParcelStore.selectedParcel"
            ></poi-list>
          </v-list-item-content>
        </v-list-item>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import ProjectFlowNavigationToolbar from '../../components/AppBar/ProjectFlowNavigationToolbar'
import Enums from '../../helpers/Enums'
import Media from '../../views/Media'
import PoiList from '../../components/RecordLists/PoiList'
import { mapState } from 'vuex'
import ParcelService from '../../services/ParcelService'

export default {
  name: 'ParcelExplore',
  props: {},
  components: {
    ProjectFlowNavigationToolbar,
    Media,
    PoiList
  },
  data: function() {
    return {
      parcel: {},
      // Snapshot taken right after hydrateParcel() normalizes `parcel` (e.g.
      // fills in properties.description on older rows that never had it).
      // Diffing against this instead of the raw `selectedParcel` avoids the
      // normalization itself being mistaken for an unsaved change.
      originalParcel: {},
      parcelId: null,
      tab: null,
      changeDetected: false,
      showMedia: false,
      roofTypes: ['flat', 'sloped']
    }
  },
  computed: {
    ...mapState('ParcelStore', ['selectedParcel']),
    parcelName: function() {
      return this.selectedParcel ? this.selectedParcel.name : ''
    },
    saveButtonDisabled: function() {
      return !this.changeDetected
    },
    getStatuses() {
      let statuses = []
      for (const property in Enums.ApartmentStatus) {
        statuses.push({
          id: Enums.ApartmentStatus[property],
          name: this.$t(`apartmentStatuses.${property}`)
        })
      }
      return statuses
    },
    // roofType is stored as a comma-separated string (a plot can permit more
    // than one roof type) but v-select's multiple mode needs an array. Kept
    // as a string on parcel.roofType itself so the JSON.stringify diff in the
    // `parcel` watcher (below) keeps comparing like-for-like against
    // selectedParcel, which the server always returns as a string.
    roofTypeArray: {
      get() {
        return this.parcel.roofType
          ? this.parcel.roofType.split(',').filter(Boolean)
          : []
      },
      set(val) {
        this.parcel.roofType = val.length ? val.join(',') : null
      }
    }
  },
  methods: {
    async generatePoi() {
      const parcel = this.$store.state.ParcelStore.selectedParcel
      const defaultPoi = await ParcelService.createDefaultPoi(parcel)
      if (defaultPoi) {
        this.$store.dispatch(
          'NotificationStore/add',
          { type: 'info', message: `Default Poi Successfully created` },
          { root: true }
        )
        // re-hydrate so the new plot POI is picked up and media becomes available
        await this.getData()
      } else {
        this.$store.dispatch(
          'NotificationStore/error',
          {
            type: 'error',
            message: `Something went wrong with creating default poi`
          },
          { root: true }
        )
      }
    },
    getData: async function() {
      if (this.parcelId) {
        await this.$store.dispatch('MediaStore/resetStore')
        await this.$store.dispatch(
          'ParcelStore/setSelectedParcel',
          this.parcelId,
          { root: true }
        )
        this.hydrateParcel()

        await this.$store.dispatch('PoiStore/setPoiContext', 'parcel')
        await this.$store.dispatch('PoiStore/fetchPois')
        // find default poi (the plot polygon record) to anchor media
        let defaultPoi = this.$store.state.PoiStore.pois.find(el => {
          return el.poiTypeId === Enums.PoiType.poi_type_plot
        })
        if (defaultPoi) {
          await this.$store.dispatch('MediaStore/resetStore')
          await this.$store.dispatch('PoiStore/setSelectedPoi', defaultPoi.id)
          await this.$store.dispatch('MediaStore/setMediaContext', 'poi')
          this.showMedia = true
        } else {
          this.showMedia = false
        }
      }
    },
    // Reassigns `parcel` from the (freshly fetched) selectedParcel and makes
    // sure properties.description exists as its own reactive key — Vue 2
    // can't track a plain `obj.description = x` assignment on a key that
    // wasn't there yet (e.g. a parcel saved before this field existed), which
    // would silently break both the v-textarea binding and the changeDetected
    // deep watcher below.
    hydrateParcel() {
      this.parcel = JSON.parse(JSON.stringify(this.selectedParcel))
      if (!this.parcel.properties) this.$set(this.parcel, 'properties', {})
      if (this.parcel.properties.description === undefined) {
        this.$set(this.parcel.properties, 'description', '')
      }
      this.originalParcel = JSON.parse(JSON.stringify(this.parcel))
    },
    save: async function() {
      await this.$store
        .dispatch('ParcelStore/updateParcel', this.parcel)
        .then(res => {
          this.$store.dispatch('ParcelStore/fetchParcels')
        })
      this.hydrateParcel()
      this.changeDetected = false
    },
    discard() {
      this.hydrateParcel()
      this.changeDetected = false
    },
    deleteParcel: async function() {
      this.$confirm({
        title: 'Delete Parcel',
        text: 'Are you sure?',
        acceptText: 'DELETE',
        cancelText: 'CANCEL'
      })
        .then(y => {
          this.$store
            .dispatch('ParcelStore/deleteParcel', this.$route.params.parcelId)
            .then(
              () => {
                this.$router.go(-1)
                this.$emit('deleted')
              },
              err => {
                this.$store.dispatch(
                  'NotificationStore/add',
                  {
                    type: 'error',
                    message:
                      'Parcel has child records attached, it cannot be deleted.'
                  },
                  { root: true }
                )
              }
            )
        })
        .catch(n => {})
    }
  },
  watch: {
    parcel: {
      deep: true,
      handler: function(after, before) {
        if (JSON.stringify(before) !== '{}') {
          this.changeDetected =
            JSON.stringify(this.parcel) !== JSON.stringify(this.originalParcel)
        }
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next(async vm => {
      vm.parcelId = to.params.parcelId
      await vm.getData()
    })
  },
  async beforeRouteUpdate(to, from, next) {
    this.parcelId = to.params.parcelId
    await this.getData()
    next()
  },
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('ParcelStore/resetStore')
    if (to.name !== 'Explore Poi') {
      this.$store.dispatch('MediaStore/resetStore')
    }
    next()
  }
}
</script>

<style scoped></style>
